import { useState } from 'react';
import { usePersonalAgents } from '../hooks/usePersonalAgents';

type TabId = 'chatgpt' | 'claude' | 'other';

// Public store listings for the app / connector.
const STORE_LINKS = {
    chatgpt: 'https://chatgpt.com/apps/turkish-airlines/asdk_app_69450d740e508191bdb697e0ac33717d',
    claude: 'https://claude.com/connectors/turkish-airlines'
} as const;

function ExternalIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
        </svg>
    );
}

function useCopy(text: string): [boolean, () => void] {
    const [done, setDone] = useState(false);
    const copy = () => {
        // navigator.clipboard is undefined outside secure contexts (plain-http LAN previews).
        const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
        if (!clipboard?.writeText) return;
        clipboard
            .writeText(text)
            .then(() => {
                setDone(true);
                setTimeout(() => setDone(false), 1800);
            })
            .catch(() => undefined);
    };
    return [done, copy];
}

export function Connect() {
    const [tab, setTab] = useState<TabId>('chatgpt');
    const personalAgents = usePersonalAgents();
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://your-domain.com';
    const url = `${origin}/mcp`;

    const tabs: Array<{ id: TabId; label: string }> = [
        { id: 'chatgpt', label: 'ChatGPT' },
        { id: 'claude', label: 'Claude' },
        { id: 'other', label: 'Cursor, VS Code & others' }
    ];

    const config = `{
  "mcpServers": {
    "turkish-airlines": {
      "url": "${url}"
    }
  }
}`;
    const [cfgDone, copyCfg] = useCopy(config);

    return (
        <section id="connect" className="tkh-section tkh-connect" aria-labelledby="connect-title">
            <div className="tkh-wrap">
                <div>
                    <p className="tkh-eyebrow">Check-in</p>
                    <h2 id="connect-title" className="tkh-h2">
                        Connect in about a minute.
                    </h2>
                    <p className="tkh-lede">The server speaks Streamable HTTP with an SSE fallback, so it plugs into any MCP-capable client. Paste one URL; there is nothing to install.</p>

                    <div className="tkh-policy">
                        <h4>Signing in</h4>
                        <p>
                            Access is secured with OAuth 2.1. When your client asks, sign in with your Miles&amp;Smiles account and approve the requested scopes once — tokens refresh silently afterwards. Discovery lives at{' '}
                            <a href="/.well-known/oauth-authorization-server" target="_blank" rel="noopener noreferrer">
                                /.well-known/oauth-authorization-server
                            </a>
                            .
                        </p>
                    </div>

                    {personalAgents && (
                        <div className="tkh-policy">
                            <h4>Personal agents</h4>
                            <p>
                                Building a personal agent? We support the Personal Agent Protocol: your agent can find us, sign members in on our page and use these tools for them. Read the <a href="/personal-agents">partner guide</a>.
                            </p>
                        </div>
                    )}
                </div>

                <div>
                    <div className="tkh-tabs" role="tablist" aria-label="Client">
                        {tabs.map((t) => (
                            <button key={t.id} type="button" role="tab" className="tkh-tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === 'chatgpt' && (
                        <div className="tkh-panel" role="tabpanel">
                            <h3>ChatGPT</h3>
                            <p className="who">Available from the ChatGPT app directory — no setup on your side</p>
                            <ol className="tkh-ol">
                                <li>
                                    <span>
                                        Open the <b>Turkish Airlines</b> app page in ChatGPT (button below) or search for it in the app directory.
                                    </span>
                                </li>
                                <li>
                                    <span>
                                        Choose <b>Add</b>. If ChatGPT asks, sign in with your Miles&amp;Smiles account and approve once.
                                    </span>
                                </li>
                                <li>
                                    <span>Ask for a flight. The answer arrives as an interactive card inside the conversation.</span>
                                </li>
                            </ol>
                            <div className="tkh-store">
                                <a href={STORE_LINKS.chatgpt} target="_blank" rel="noopener noreferrer" className="tkh-btn tkh-btn-primary">
                                    Turkish Airlines on ChatGPT <ExternalIcon />
                                </a>
                            </div>
                        </div>
                    )}

                    {tab === 'claude' && (
                        <div className="tkh-panel" role="tabpanel">
                            <h3>Claude</h3>
                            <p className="who">Available from the Claude connector directory — works in claude.ai and Claude Desktop</p>
                            <ol className="tkh-ol">
                                <li>
                                    <span>
                                        Open the <b>Turkish Airlines</b> connector page (button below) or find it under <b>Settings → Connectors → Browse</b>.
                                    </span>
                                </li>
                                <li>
                                    <span>
                                        Choose <b>Connect</b>. If Claude asks, sign in with your Miles&amp;Smiles account and approve once.
                                    </span>
                                </li>
                                <li>
                                    <span>Enable the connector in a chat and ask away — flight cards render right in the thread.</span>
                                </li>
                            </ol>
                            <div className="tkh-store">
                                <a href={STORE_LINKS.claude} target="_blank" rel="noopener noreferrer" className="tkh-btn tkh-btn-primary">
                                    Turkish Airlines on Claude <ExternalIcon />
                                </a>
                            </div>
                        </div>
                    )}

                    {tab === 'other' && (
                        <div className="tkh-panel" role="tabpanel">
                            <h3>Cursor, VS Code, Claude Code, Windsurf…</h3>
                            <p className="who">Any client that speaks Streamable HTTP</p>
                            <ol className="tkh-ol">
                                <li>
                                    <span>Add the server to your client’s MCP configuration (most accept the JSON below).</span>
                                </li>
                                <li>
                                    <span>When the server asks for authorization, your client opens the Turkish Airlines login: membership number, password, and a one-time code if your account requires it.</span>
                                </li>
                                <li>
                                    <span>Approve the requested scopes once. Tokens refresh silently for 30 days.</span>
                                </li>
                            </ol>
                            <pre className="tkh-pre">
                                {'{\n  '}
                                <span className="k">"mcpServers"</span>
                                {': {\n    '}
                                <span className="k">"turkish-airlines"</span>
                                {': {\n      '}
                                <span className="k">"url"</span>
                                {': '}
                                <span className="s">"{url}"</span>
                                {'\n    }\n  }\n}'}
                            </pre>
                            <div className="tkh-endpoint">
                                <code>mcp.json</code>
                                <button type="button" className="tkh-copy" data-done={cfgDone} onClick={copyCfg} aria-label="Copy configuration">
                                    {cfgDone ? 'Copied' : 'Copy config'}
                                </button>
                            </div>
                            <p className="tkh-note">
                                Claude Code: <b>claude mcp add --transport http turkish-airlines {url}</b>. Authorization code flow with PKCE (S256), dynamic client registration and token revocation are all supported.{' '}
                                <a href="/.well-known/oauth-protected-resource/mcp" target="_blank" rel="noopener noreferrer">
                                    Protected-resource metadata
                                </a>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
